import type { PlatformProductStatus } from '~/types/graphql';
import type { Campaign } from '~/modules/campaign/types';
import type { BadgeProps } from '@shopify/polaris';

import {
  PRICE_PRODUCT_ISSUE,
  PRICE_PRODUCT_ISSUE_BADGE_LABEL,
  PRICE_PRODUCT_STATUS_BADGE_LABEL,
  PRICE_PRODUCT_STATUS_BADGE_TONE,
} from '../constants';
import { CAMPAIGN_ENDED_STATUS, CAMPAIGN_TYPE } from '~/constants/campaign';
import { findVariantByOrigin } from '~/modules/campaign';

export type IPriceProductIssue = (typeof PRICE_PRODUCT_ISSUE)[keyof typeof PRICE_PRODUCT_ISSUE];

export interface IPriceProductStatusSignals {
  status?: PlatformProductStatus;
  isDeleted: boolean;
  isPublished: boolean;
  /** Defaults to Control. The Variant product is a duplicate the app itself keeps off every sales
   * channel on purpose, so being unpublished there is the expected state, not an issue — only Control
   * has to actually be live on the online store for traffic to reach it. */
  isControl?: boolean;
}

export interface IPriceProductBadge {
  label: string;
  tone?: BadgeProps['tone'];
}

/**
 * Deleted beats draft/archived beats unpublished: a draft or archived product carries no Online Store
 * URL either — Shopify never publishes a draft to any channel — so that status has to be read off first,
 * or every draft/archived product would misreport as merely "unpublished" instead.
 */
export const getPriceProductIssue = ({
  status,
  isDeleted,
  isPublished,
  isControl = true,
}: IPriceProductStatusSignals): IPriceProductIssue | undefined => {
  if (isDeleted) return PRICE_PRODUCT_ISSUE.DELETED;
  if (status === 'DRAFT' || status === 'ARCHIVED') return PRICE_PRODUCT_ISSUE.DRAFT_OR_ARCHIVED;
  // Unlisted is not itself an issue — the campaign is only ever reached via its direct Product URL, never
  // storefront discovery, and an unlisted product keeps `onlineStoreURL`/`publishedAt` (`isPublished`)
  // regardless. Only a product missing from the Online Store channel entirely blocks the redirect.
  if (isControl && !isPublished) return PRICE_PRODUCT_ISSUE.UNPUBLISHED;

  return undefined;
};

/** Active carries no badge; every other status, deleted or unpublished shows one. */
export const getPriceProductBadge = (
  signals: IPriceProductStatusSignals,
): IPriceProductBadge | undefined => {
  if (signals.isDeleted) return { label: PRICE_PRODUCT_ISSUE_BADGE_LABEL.DELETED };
  if (signals.status && signals.status !== 'ACTIVE') {
    return {
      label: PRICE_PRODUCT_STATUS_BADGE_LABEL[signals.status],
      tone: PRICE_PRODUCT_STATUS_BADGE_TONE[signals.status],
    };
  }
  if (!signals.isPublished) return { label: PRICE_PRODUCT_ISSUE_BADGE_LABEL.UNPUBLISHED };

  return undefined;
};

/** First issue found, in the fixed priority `getPriceProductIssue` already orders a single product by. */
export const getFirstPriceProductIssue = (
  rows: IPriceProductStatusSignals[],
): IPriceProductIssue | undefined => {
  const issues = rows.map(getPriceProductIssue);

  return (
    issues.find((issue) => issue === PRICE_PRODUCT_ISSUE.DELETED) ??
    issues.find((issue) => issue === PRICE_PRODUCT_ISSUE.DRAFT_OR_ARCHIVED) ??
    issues.find((issue) => issue === PRICE_PRODUCT_ISSUE.UNPUBLISHED)
  );
};

/**
 * Shared by `usePriceProductStatus` and `useCampaignPriceListingProductIssue`, which only differ in
 * where a baseID's signal comes from — a direct fetch vs. the listing's already-fetched map. This owns
 * the part both need identically: resolving a PRICE campaign down to its unique Control/Variant baseIDs
 * and reducing their signals to the one issue to show.
 */
export interface IResolvePriceProductIssueParams {
  campaign?: Campaign;
  getSignal: (baseID: string) => Omit<IPriceProductStatusSignals, 'isControl'> | undefined;
}

export const resolvePriceProductIssue = ({
  campaign,
  getSignal,
}: IResolvePriceProductIssueParams): IPriceProductIssue | undefined => {
  if (campaign?.type !== CAMPAIGN_TYPE.PRICE) return undefined;

  const controlBaseID = findVariantByOrigin(campaign, true)?.price?.baseID;
  // Ending an experiment archives (or deletes) its Variant duplicate on purpose, so that product's
  // status says nothing the merchant needs to act on anymore — only the Control is still read.
  const isCampaignEnded = !!campaign.status && CAMPAIGN_ENDED_STATUS.includes(campaign.status);

  const baseIDs = new Set<string>();
  campaign.variants?.forEach((variant) => {
    const baseID = variant?.price?.baseID;
    if (!baseID || (isCampaignEnded && baseID !== controlBaseID)) return;

    baseIDs.add(baseID);
  });

  const signals: IPriceProductStatusSignals[] = [];
  baseIDs.forEach((baseID) => {
    const signal = getSignal(baseID);
    if (signal) signals.push({ ...signal, isControl: baseID === controlBaseID });
  });

  return getFirstPriceProductIssue(signals);
};
